// File: frontend/src/pages/exams/ResultCard.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

const ResultCard = () => {
  const { school } = useSchool();
  const { studentId, examId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch complete student report card details
   */
  const fetchReportCard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/exams/reportcard/${studentId}/${examId}`);
      if (response.data?.success) {
        setData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load report card.');
      }
    } catch (err) {
      console.error('Error fetching report card:', err);
      setError(err.response?.data?.message || err.message || 'Error loading report card.');
    } finally {
      setLoading(false);
    }
  }, [studentId, examId]);

  useEffect(() => {
    fetchReportCard();
  }, [fetchReportCard]);

  /**
   * Trigger browser print dialog
   */
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500 font-medium">Generating Report Card...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 flex flex-col items-center justify-center">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 text-center max-w-md w-full shadow-sm">
          <p className="text-red-600 font-bold mb-4">{error || 'Report Card not found.'}</p>
          <Link to="/exams" className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-semibold">
            Return to Examinations
          </Link>
        </div>
      </div>
    );
  }

  const { student, exam, results, summary } = data;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center py-8 px-4 print:p-0 print:bg-white">
      
      {/* Top Action Bar (Hidden on print) */}
      <div className="max-w-4xl w-full flex items-center justify-between mb-6 print:hidden">
        <Link
          to={`/exams/results/${examId}`}
          className="bg-white text-slate-700 font-semibold px-4 py-2 rounded-xl text-sm border border-slate-200 shadow-sm hover:bg-slate-50 transition"
        >
          ← Back to Results
        </Link>
        <button
          onClick={handlePrint}
          className="bg-primary hover:bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl text-sm shadow-md transition flex items-center gap-2"
        >
          🖨️ Print Report Card
        </button>
      </div>

      {/* Printable A4 Report Card Container */}
      <div className="bg-white max-w-4xl w-full rounded-2xl border border-slate-200 p-8 md:p-12 shadow-lg print:shadow-none print:border-none print:max-w-none print:w-full print:p-6 print:rounded-none">
        
        {/* School Header */}
        <div className="text-center border-b-2 border-slate-800 pb-6 mb-6">
          {school?.logo ? (
            <img src={school.logo} alt="School Logo" className="w-16 h-16 object-contain mx-auto mb-3" />
          ) : (
            <div className="w-16 h-16 bg-primary text-white rounded-2xl mx-auto flex items-center justify-center font-extrabold text-2xl mb-3 shadow-md print:border">
              SMS
            </div>
          )}
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight uppercase">
            {school?.name || 'SCHOOL PORTAL'}
          </h1>
          <p className="text-xs text-slate-500 font-medium mt-1">
            Official Student Progress Report Card • Academic Session {school?.session_year || '2026'}
          </p>
        </div>

        {/* Examination Title */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center mb-6">
          <h2 className="font-extrabold text-slate-800 text-base uppercase tracking-wider">
            {exam?.name || 'TERMINAL EXAMINATION'} - {exam?.term || 'TERM REPORT'}
          </h2>
        </div>

        {/* Student Metadata Card */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50/70 rounded-xl border border-slate-200 text-xs mb-6">
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Student Name</span>
            <span className="font-bold text-slate-900 text-sm">{student?.student_name}</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Roll Number</span>
            <span className="font-mono font-bold text-slate-900 text-sm">{student?.roll_no || student?.student_id}</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Class & Section</span>
            <span className="font-bold text-slate-900 text-sm">{student?.class_name || `Class ${student?.class_id}`}</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Father Name</span>
            <span className="font-bold text-slate-900 text-sm">{student?.father_name || '-'}</span>
          </div>
        </div>

        {/* Results Table */}
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-left text-xs border border-slate-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-slate-800 text-white uppercase font-bold text-[11px]">
                <th className="py-3 px-4">Subject Name</th>
                <th className="py-3 px-3 text-center">Code</th>
                <th className="py-3 px-3 text-right">Max Marks</th>
                <th className="py-3 px-3 text-right">Pass Marks</th>
                <th className="py-3 px-3 text-right">Obtained</th>
                <th className="py-3 px-3 text-center">Grade</th>
                <th className="py-3 px-4">Remarks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
              {results.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-6 text-center text-slate-400">No subject marks recorded.</td>
                </tr>
              ) : (
                results.map((res) => (
                  <tr key={res.id} className="hover:bg-slate-50">
                    <td className={`py-3 px-4 font-bold text-slate-900 ${res.subject_name?.toLowerCase().includes('urdu') ? 'lang-urdu' : ''}`}>
                      {res.subject_name}
                    </td>
                    <td className="py-3 px-3 text-center font-mono text-slate-500">{res.subject_code}</td>
                    <td className="py-3 px-3 text-right font-mono">{res.total_marks}</td>
                    <td className="py-3 px-3 text-right font-mono text-slate-500">{res.pass_marks}</td>
                    <td className="py-3 px-3 text-right font-mono font-bold text-slate-900">{res.marks_obtained}</td>
                    <td className="py-3 px-3 text-center">
                      <span className="font-bold px-2 py-0.5 bg-slate-100 rounded text-slate-900 border">
                        {res.grade}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-500 italic">{res.remarks || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Footer Box */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-5 bg-slate-900 text-white rounded-xl mb-12">
          <div>
            <span className="text-slate-400 text-[11px] uppercase block">Grand Total Marks</span>
            <span className="font-mono text-lg font-extrabold">{summary?.total_obtained} / {summary?.total_max}</span>
          </div>
          <div>
            <span className="text-slate-400 text-[11px] uppercase block">Percentage</span>
            <span className="font-mono text-lg font-extrabold text-blue-400">{summary?.percentage}%</span>
          </div>
          <div>
            <span className="text-slate-400 text-[11px] uppercase block">Overall Grade</span>
            <span className="font-mono text-lg font-extrabold text-emerald-400">{summary?.final_grade}</span>
          </div>
          <div>
            <span className="text-slate-400 text-[11px] uppercase block">Final Result</span>
            <span className={`inline-block text-sm font-extrabold px-3 py-0.5 rounded ${
              summary?.status === 'PASS' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
            }`}>
              {summary?.status}
            </span>
          </div>
        </div>

        {/* Result Disclaimer Footer */}
        {school?.result_footer && (
          <p className="text-center text-[11px] font-sans italic text-slate-500 mb-8">
            {school.result_footer}
          </p>
        )}

        {/* Signature Blocks */}
        <div className="grid grid-cols-2 gap-8 pt-8 border-t border-slate-200">
          <div className="text-center">
            <div className="h-12 border-b border-slate-400 max-w-[200px] mx-auto mb-2"></div>
            <span className="text-xs font-bold text-slate-700 uppercase">Class Teacher Signature</span>
          </div>
          <div className="text-center flex flex-col items-center">
            <div className="h-12 border-b border-slate-400 max-w-[200px] w-full mb-2 flex items-end justify-center gap-4">
              {school?.stamp && (
                <img src={school.stamp} alt="Stamp" className="h-10 object-contain opacity-80" />
              )}
              {school?.principal_signature && (
                <img src={school.principal_signature} alt="Signature" className="h-10 object-contain" />
              )}
            </div>
            <span className="text-xs font-bold text-slate-700 uppercase">
              {school?.principal_name || 'Principal'} Signature & Stamp
            </span>
          </div>
        </div>

      </div>

    </div>
  );
};

export default ResultCard;
